import { useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Loader2, Unplug, Phone, FileText, Calendar } from "lucide-react";
import { toast } from "react-toastify";
import { desconectarConta } from "../services/whatsappOficialApi";

const QUALITY_STYLES = {
  GREEN: "bg-emerald-950 text-emerald-300 border-emerald-800",
  YELLOW: "bg-amber-950 text-amber-300 border-amber-800",
  RED: "bg-red-950 text-red-300 border-red-800",
};

const STATUS_STYLES = {
  ativo: { Icon: CheckCircle2, color: "text-emerald-400", label: "Ativo" },
  suspenso: { Icon: AlertTriangle, color: "text-amber-400", label: "Suspenso pela Meta" },
  desconectado: { Icon: XCircle, color: "text-ink-4", label: "Desconectado" },
};

export default function ContaCard({ conta, onDisconnected }) {
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);

  const status = STATUS_STYLES[conta.status] || STATUS_STYLES.desconectado;
  const StatusIcon = status.Icon;

  async function handleDisconnect() {
    setLoading(true);
    try {
      await desconectarConta(conta.id);
      toast.success("Conta desconectada");
      onDisconnected?.();
    } catch (err) {
      toast.error("Falha ao desconectar: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
      setConfirming(false);
    }
  }

  const dataAtt = conta.data_atualizacao ? new Date(conta.data_atualizacao).toLocaleString("pt-BR") : null;

  return (
    <div className="bg-surface-1 border border-white/[0.06] rounded-lg p-6 max-w-2xl">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <StatusIcon className={`w-5 h-5 ${status.color}`} />
            <span className={`text-sm font-medium ${status.color}`}>{status.label}</span>
          </div>
          <h2 className="text-2xl font-semibold text-white">{conta.display_phone_number || "—"}</h2>
          <p className="text-sm text-ink-3">{conta.verified_name || "Sem nome verificado"}</p>
        </div>

        {conta.quality_rating && (
          <span className={`text-xs font-medium border rounded px-2 py-1 ${QUALITY_STYLES[conta.quality_rating] || ""}`}>
            Qualidade: {conta.quality_rating}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Stat icon={<Phone className="w-4 h-4" />} label="Phone Number ID" value={conta.phone_number_id} mono />
        <Stat icon={<FileText className="w-4 h-4" />} label="Templates" value={conta.templates_count ?? 0} />
        <Stat icon={<Calendar className="w-4 h-4" />} label="Última atualização" value={dataAtt || "—"} />
      </div>

      <div className="text-xs text-ink-4 mb-4 space-y-1">
        <div>WABA ID: <code className="bg-base px-1 rounded text-ink-3">{conta.waba_id}</code></div>
        {conta.app_id && <div>App ID: <code className="bg-base px-1 rounded text-ink-3">{conta.app_id}</code></div>}
        {conta.business_id && <div>Business ID: <code className="bg-base px-1 rounded text-ink-3">{conta.business_id}</code></div>}
      </div>

      {!confirming ? (
        <button
          onClick={() => setConfirming(true)}
          className="inline-flex items-center gap-2 bg-surface-2 hover:bg-red-900 text-white px-4 py-2 rounded text-sm font-medium transition"
        >
          <Unplug className="w-4 h-4" /> Desconectar
        </button>
      ) : (
        <div className="bg-red-950 border border-red-900 rounded p-3 flex items-center gap-3 flex-wrap">
          <span className="text-sm text-red-200">
            Tem certeza? A Meta vai parar de mandar eventos pra essa WABA.
          </span>
          <button
            onClick={handleDisconnect}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 disabled:bg-surface-3 text-white px-3 py-1 rounded text-sm font-medium transition inline-flex items-center gap-1"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Sim, desconectar
          </button>
          <button
            onClick={() => setConfirming(false)}
            disabled={loading}
            className="text-sm text-ink-2 hover:text-white"
          >
            Cancelar
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value, mono }) {
  return (
    <div className="bg-base border border-white/[0.06] rounded p-3">
      <div className="flex items-center gap-1 text-xs text-ink-4 mb-1">
        {icon} {label}
      </div>
      <div className={`text-sm text-white ${mono ? "font-mono" : ""} truncate`}>{value}</div>
    </div>
  );
}
